import { ArrowRight, Check, CircleCheckBig } from "lucide-react";

import { Badge } from "@design-lab/shared/ui";
import { StarsIcon } from "@design-lab/shared/ui/icons";
import { ArtistEditsSlaDisclosure } from "common/ArtistEditsSlaDisclosure";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import { useArtistEditsSlaDisclosure } from "@design-lab/shared/lib/config";

const BEFORE_IMAGE = remoteImageStaticUrl("2/gen/user-retention-modal-pin-before.png", {
  w: 275,
  h: 275
});
const AFTER_IMAGE = remoteImageStaticUrl("2/gen/user-retention-modal-pin-after.png", {
  w: 275,
  h: 275
});

const SAMPLE_MESSAGES = [
  'Add the text "#1 Fan" in white color onto the blue flag.',
  "Change the color of the dog's t-shirt from blue to red."
];

const AI_EDITS = ["Text Added", "Color Changed"];

const FEATURES = [
  "Option to send to human artist",
  "Background removal",
  "Add text, change color",
  "Direct print, fast delivery"
];

export function ArtistEditsInfo() {
  const showSlaDisclosure = useArtistEditsSlaDisclosure();

  return (
    <div data-testid="artist-edits-info" className="font-inter space-y-4">
      <div>
        <h2 className="text-base font-bold leading-6">AI Edits — How it works</h2>
        <p className="text-xs leading-[130%] text-gray-600">
          AI analyzes your edit request and decides if it can complete automatically or requires our{" "}
          <strong>Free</strong> professional edit service, which happens after checkout.
        </p>
        {showSlaDisclosure ? (
          <ArtistEditsSlaDisclosure className="mt-1 space-y-0.5 text-xs font-medium leading-[130%] text-gray-700" />
        ) : null}
      </div>

      <div className="border-3 relative flex rounded-xl border-gray-200 p-1.5">
        {/* Arrow */}
        <div className="absolute left-1/2 top-1/3 z-10 -translate-x-1/2">
          <div className="bg-foreground flex size-8 items-center justify-center rounded-full shadow-md">
            <ArrowRight className="text-background size-5" strokeWidth={2.5} />
          </div>
        </div>

        {/* Left side - Original */}
        <div className="flex flex-1 flex-col gap-3 px-0 py-2">
          <div className="flex flex-col items-center gap-2">
            <p className="h-6 text-center text-xs font-medium leading-6">Original Upload</p>
            <img
              src={BEFORE_IMAGE}
              alt="Before AI edits"
              className="w-full max-w-[180px] object-cover"
            />
          </div>
          <div className="space-y-2">
            <p className="text-center text-xs font-bold leading-4 text-gray-800">Sample message:</p>
            {SAMPLE_MESSAGES.map((message, index) => (
              <div
                key={message}
                data-testid={`sample-message-${index}`}
                className="flex items-start gap-2"
              >
                <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                  {index + 1}
                </div>
                <p className="text-[11px] font-medium leading-4 text-gray-600">{message}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right side - AI Edited */}
        <div className="flex flex-1 flex-col gap-3 rounded-lg bg-purple-100 px-0 py-2">
          <div className="flex flex-col items-center gap-2">
            <div className="flex h-6 items-center gap-2">
              <StarsIcon className="size-6" />
              <p className="text-xs font-bold leading-4">AI Edited</p>
              <Badge
                data-testid="artist-edits-free-badge"
                size="sm"
                className="bg-green-700 font-medium text-white"
              >
                Free
              </Badge>
            </div>
            <img
              src={AFTER_IMAGE}
              alt="After AI edits"
              className="w-full max-w-[180px] object-cover mix-blend-multiply"
            />
          </div>
          <div className="space-y-2">
            <p className="text-center text-xs font-bold leading-4 text-gray-800">AI Edits:</p>
            <div className="flex justify-center">
              <div className="flex flex-col gap-2">
                {AI_EDITS.map(edit => (
                  <div
                    key={edit}
                    data-testid={`artist-edit-${edit}`}
                    className="flex items-center gap-2"
                  >
                    <CircleCheckBig className="size-5 shrink-0 fill-green-700 stroke-white" />
                    <p className="text-[11px] font-bold leading-4 text-gray-800">{edit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features section */}
      <div className="space-y-3">
        <p className="text-lg font-bold">What else do you get?</p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {FEATURES.map(feature => (
            <div className="flex items-center gap-1.5" key={feature}>
              <Check className="size-4 shrink-0 stroke-green-700" />
              <p className="whitespace-nowrap text-[11px] font-medium leading-6 text-gray-900">
                {feature}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
